<template>
  <v-dialog v-model="open" max-width="640" no-click-animation @keydown.esc="close">
    <v-card>
      <v-card-title>
        <v-icon color="primary" class="me-2">mdi-bell-outline</v-icon>
        <span class="text-h6">{{ $t('alerts_for', { name: studentName }) }}</span>
      </v-card-title>
      <v-card-subtitle class="pt-2">{{ $t('alerts_hint') }}</v-card-subtitle>

      <v-card-text>
        <vue-element-loading :active="isLoading" />

        <!-- the commonest reason a family gets nothing, and it is not a switch -->
        <v-alert v-if="familyWithoutDevice.length" type="warning" text dense class="mb-4">
          {{ $t('family_never_signed_in', { names: familyWithoutDevice.join(', ') }) }}
        </v-alert>

        <v-switch
          v-for="field in fields"
          :key="field"
          v-model="settings[field]"
          :label="$t('alert_' + field)"
          :disabled="isSaving"
          dense
          inset
          hide-details
          class="mt-2"
        ></v-switch>

        <v-text-field
          v-model.number="settings.bus_near_pickup_location_notification_by_distance"
          :label="$t('alert_distance_metres')"
          :hint="$t('alert_distance_hint')"
          persistent-hint
          type="number"
          min="0"
          max="10000"
          dense
          outlined
          class="mt-5"
        ></v-text-field>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text :disabled="isSaving" @click="close">{{ $t('close') }}</v-btn>
        <v-btn color="primary" text :loading="isSaving" @click="save">{{ $t('save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
/*
 * Which alerts a child's family actually gets.
 *
 * "We are not receiving notifications" is one of the commonest things a school
 * is telephoned about, and the school had no way to look: the switches live on
 * the student's settings and are set from the parent's app. So the school could
 * neither see them nor change them, and the call ended with "try reinstalling".
 *
 * The likeliest answer is not a switch at all - a phone that has never signed
 * in has no token to send to - so that is said at the top rather than left to
 * be deduced.
 */
import VueElementLoading from "vue-element-loading";

export default {
  name: "StudentAlertsDialog",
  components: { VueElementLoading },
  props: {
    value: { type: Boolean, default: false },
    student: { type: Object, default: null },
  },
  data() {
    return {
      fields: [
        "next_stop_is_your_pickup_location_notification_on_off",
        "bus_arrived_at_pickup_location_notification_on_off",
        "student_is_picked_up_notification_on_off",
        "student_is_missed_pickup_notification_on_off",
        "bus_arrived_at_school_notification_on_off",
        "bus_near_drop_off_location_notification_on_off",
        "bus_arrived_at_drop_off_location_notification_on_off",
      ],
      settings: {},
      family: [],
      isLoading: false,
      isSaving: false,
    };
  },
  computed: {
    open: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    studentName() {
      return this.student ? this.student.name : "";
    },
    familyWithoutDevice() {
      return this.family.filter((member) => !member.has_device).map((member) => member.name);
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) this.load();
    },
  },
  methods: {
    close() {
      this.open = false;
    },
    load() {
      if (!this.student) return;

      this.isLoading = true;
      this.settings = {};
      this.family = [];

      axios
        .get("/school-students/notification-settings", {
          params: { student_id: this.student.id },
        })
        .then((response) => {
          this.settings = response.data.settings || {};
          this.family = response.data.family || [];
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_loading_alerts"));
        })
        .then(() => {
          this.isLoading = false;
        });
    },
    save() {
      this.isSaving = true;
      axios
        .post(
          "/school-students/notification-settings",
          Object.assign({ student_id: this.student.id }, this.settings)
        )
        .then(() => {
          this.$notifySuccess(this.$t("saved"));
          this.open = false;
        })
        .catch((error) => {
          this.$notifyError(error, this.$t("error_saving"));
        })
        .then(() => {
          this.isSaving = false;
        });
    },
  },
};
</script>
